import apiCall from './api';

export const saleService = {
  
  
  /**
   * Récupérer toutes les ventes de la boutique
   */
  getSales: async () => {
    try {
      
      const response = await apiCall.get('/employee/sales');
      
      if (response.success && Array.isArray(response.data)) {
        
        const formattedSales = response.data.map(sale => 
          saleService.formatSaleForDisplay(sale)
        ).filter(sale => sale !== null);
        
        
        return {
          success: true,
          data: formattedSales,
          message: `${formattedSales.length} vente(s) récupérée(s)`
        };
      }
      
      return { 
        success: false, 
        data: [], 
        message: 'Aucune vente trouvée' 
      };
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des ventes:', error);
      return { 
        success: false, 
        data: [], 
        message: 'Erreur de connexion: ' + error.message 
      };
    }
  },

  /**
   * ✅ NOUVEAU : Récupérer les ventes avec pagination + filtres côté serveur
   * C'est LE remplacement principal de getSales() pour SalesList.js
   * 
   * @param {number} page - Page (0-indexed)
   * @param {number} size - Nombre par page
   * @param {object} filters - { dateFilter, status, payment, search, showCancelled, startDate, endDate }
   */
  getSalesFiltered: async (page = 0, size = 20, filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', size);
      
      if (filters.dateFilter) params.append('dateFilter', filters.dateFilter);
      if (filters.status) params.append('status', filters.status);
      if (filters.payment) params.append('payment', filters.payment);
      if (filters.search) params.append('search', filters.search);
      if (filters.showCancelled) params.append('showCancelled', 'true');
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await apiCall.get(`/employee/sales/filtered?${params.toString()}`);

      if (response.success && response.data) {
        const salesData = response.data.sales || response.data;
        const content = salesData.content || [];

        // Formater uniquement les ventes de la page courante
        const formattedSales = content.map(sale =>
          saleService.formatSaleForDisplay(sale)
        ).filter(sale => sale !== null);

        return {
          success: true,
          data: formattedSales,
          pagination: {
            currentPage: salesData.page_number ?? salesData.pageNumber ?? 0,
            totalPages: salesData.total_pages ?? salesData.totalPages ?? 1,
            totalElements: salesData.total_elements ?? salesData.totalElements ?? 0,
            pageSize: salesData.page_size ?? salesData.pageSize ?? size,
            hasNext: salesData.has_next ?? salesData.hasNext ?? false,
            hasPrevious: salesData.has_previous ?? salesData.hasPrevious ?? false,
          },
          // ✅ NOUVEAU : Stats filtrées venant du serveur
          filteredRevenue: response.data.filteredRevenue ?? 0,
          filteredCount: response.data.filteredCount ?? 0,
          message: `${formattedSales.length} vente(s) récupérée(s)`
        };
      }

      return { success: false, data: [], pagination: null, message: 'Aucune vente trouvée' };

    } catch (error) {
      console.error('❌ Erreur ventes filtrées:', error);
      return { success: false, data: [], pagination: null, message: error.message };
    }
  },

  /**
   * ✅ NOUVEAU : Récupérer les statistiques agrégées côté serveur
   * Remplace le calcul JS des compteurs, revenus, etc.
   * 
   * @param {string} dateFilter - 'today', 'week', 'month', 'year', 'all'
   * @param {string} startDate - ISO date (optionnel, pour dates personnalisées)
   * @param {string} endDate - ISO date (optionnel)
   */
  getSalesStatsAggregated: async (dateFilter = 'today', startDate = null, endDate = null) => {
    try {
      const params = new URLSearchParams();
      params.append('dateFilter', dateFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiCall.get(`/employee/sales/stats-aggregated?${params.toString()}`);

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data
        };
      }

      return { success: false, data: null };

    } catch (error) {
      console.error('❌ Erreur stats agrégées:', error);
      return { success: false, data: null, message: error.message };
    }
  },

  /**
   * Récupérer les ventes du jour uniquement — chargement rapide
   */
  getSalesToday: async () => {
    try {
      const response = await apiCall.get('/employee/sales/today');

      if (response.success && Array.isArray(response.data)) {
        const formattedSales = response.data.map(sale =>
          saleService.formatSaleForDisplay(sale)
        ).filter(sale => sale !== null);

        return {
          success: true,
          data: formattedSales,
          message: `${formattedSales.length} vente(s) du jour`
        };
      }

      return { success: true, data: [], message: 'Aucune vente aujourd\'hui' };

    } catch (error) {
      console.error('❌ Erreur getSalesToday:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  /**
   * ✅ NOUVEAU : Récupérer les ventes avec vraie pagination serveur
   * Utilisé par SalesList.js pour un chargement rapide
   */
  getSalesPaginated: async (page = 0, size = 20) => {
    try {
      const response = await apiCall.get(`/employee/sales/paginated?page=${page}&size=${size}`);

      if (response.success && response.data) {
        const pageData = response.data;

        // Formater uniquement les ventes de la page courante (pas des milliers)
        const formattedSales = (pageData.content || []).map(sale =>
          saleService.formatSaleForDisplay(sale)
        ).filter(sale => sale !== null);

        return {
          success: true,
          data: formattedSales,
          pagination: {
            currentPage: pageData.page_number ?? pageData.pageNumber ?? 0,
            totalPages: pageData.total_pages ?? pageData.totalPages ?? 1,
            totalElements: pageData.total_elements ?? pageData.totalElements ?? 0,
            pageSize: pageData.page_size ?? pageData.pageSize ?? size,
            hasNext: pageData.has_next ?? pageData.hasNext ?? false,
            hasPrevious: pageData.has_previous ?? pageData.hasPrevious ?? false,
            isFirst: pageData.is_first ?? pageData.isFirst ?? true,
            isLast: pageData.is_last ?? pageData.isLast ?? true,
          },
          message: `${formattedSales.length} vente(s) récupérée(s)`
        };
      }

      return {
        success: false,
        data: [],
        pagination: null,
        message: 'Aucune vente trouvée'
      };

    } catch (error) {
      console.error('❌ Erreur ventes paginées:', error);
      return {
        success: false,
        data: [],
        pagination: null,
        message: 'Erreur de connexion: ' + error.message
      };
    }
  },

  /**
 * ✅ NOUVELLE MÉTHODE : Payer un crédit
 * À ajouter dans votre saleService.js
 */
payCreditSale: async (saleId, paymentData) => {
  try {
    
    // Validation des données
    if (!saleId) {
      throw new Error('ID de vente requis');
    }
    
    if (!paymentData.amount || paymentData.amount <= 0) {
      throw new Error('Montant de paiement requis et positif');
    }
    
    if (!paymentData.paymentMethod) {
      throw new Error('Méthode de paiement requise');
    }
    
    // Formater les données selon UpdatePaymentRequest.java
    const backendPayload = {
      payment_method: paymentData.paymentMethod,
      credit_payment: parseFloat(paymentData.amount)
    };
    
    
    // Appel API vers l'endpoint payment
    const response = await apiCall.put(`/employee/sales/${saleId}/payment`, backendPayload);
    
    if (response.success) {
      return {
        success: true,
        data: response.data,
        message: 'Paiement encaissé avec succès'
      };
    }
    
    return {
      success: false,
      message: response.message || 'Erreur lors du paiement'
    };
    
  } catch (error) {
    console.error('❌ Erreur lors du paiement crédit:', error);
    
    // Gestion des erreurs spécifiques
    if (error.response?.status === 400) {
      return {
        success: false,
        message: error.response.data?.message || 'Données de paiement invalides'
      };
    }
    
    if (error.response?.status === 404) {
      return {
        success: false,
        message: 'Vente non trouvée'
      };
    }
    
    if (error.response?.status === 500) {
      return {
        success: false,
        message: 'Erreur serveur lors du traitement du paiement'
      };
    }
    
    return {
      success: false,
      message: error.message || 'Erreur de connexion'
    };
  }
},

  /**
   * ✅ NOUVEAU : Validation du payload backend
   */
  validateBackendPayload: (payload) => {
  
  const errors = [];
  
  // Validation client
  if (!payload.customer_name || !payload.customer_name.trim()) {
    errors.push('customer_name requis');
  }
  
  // Validation articles
  if (!Array.isArray(payload.items) || payload.items.length === 0) {
    errors.push('items requis et non vide');
  } else {
    payload.items.forEach((item, index) => {
      if (!item.product_id) {
        errors.push(`items[${index}].product_id requis`);
      }
      if (!item.quantity || item.quantity <= 0) {
        errors.push(`items[${index}].quantity invalide`);
      }
      if (item.unit_price === undefined || item.unit_price <= 0) {
        errors.push(`items[${index}].unit_price invalide`);
      }
    });
  }
  
  // Validation paiement
  if (!payload.payment_method) {
    errors.push('payment_method requis');
  }
  
  if (errors.length > 0) {
    console.error('❌ Erreurs de validation:', errors);
  } else {
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
},

  /**
   * ✅ CORRECTION : Formater les données pour l'envoi au backend
   */
  formatSaleForBackend: (saleData) => {
  
  // Validation des données requises
  if (!saleData.customer?.name) {
    throw new Error('Nom du client requis');
  }
  
  if (!saleData.items || saleData.items.length === 0) {
    throw new Error('Au moins un article requis');
  }
  
  if (!saleData.paymentMethod) {
    throw new Error('Méthode de paiement requise');
  }
  
  // ✅ LOGIQUE CRITIQUE : Si paymentMethod = CREDIT, forcer isCredit à true
  let isCredit = saleData.isCredit || false;
  
  if (saleData.paymentMethod === 'CREDIT') {
    isCredit = true;
  }
  
  
  
  // ✅ Structure avec les noms exacts attendus par CreateSaleRequest.java
  const backendPayload = {
    // Champs avec @JsonProperty (snake_case)
    customer_name: saleData.customer.name.trim(),
    customer_phone: saleData.customer.phone?.trim() || null,
    payment_method: saleData.paymentMethod,
    is_credit: isCredit, // ✅ Utiliser la valeur vérifiée
    
    // ✅ NOUVEAU : Champs revendeur pour nouveau client
    is_reseller: saleData.customer.isReseller || false,
    reseller_discount: saleData.customer.resellerDiscount || null,
    
    // Articles
    items: saleData.items.map(item => ({
      product_id: item.productId || item.id,
      product_name: item.name || item.productName,
      quantity: parseInt(item.quantity),
      unit_price: parseFloat(item.price || item.unitPrice),
      total_price: parseFloat((item.quantity || 1) * (item.price || item.unitPrice || 0))
    })),
    
    // Champs optionnels
    discount: saleData.discount ? parseFloat(saleData.discount) : 0,
    tax: saleData.tax ? parseFloat(saleData.tax) : 0,
    notes: saleData.notes?.trim() || null
  };
  
  // Log si nouveau client revendeur
  if (backendPayload.is_reseller) {
    
  }
  
  
  return backendPayload;
},

  /**
   * ✅ Méthodes utilitaires pour l'affichage
   */
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  },

  formatDate: (dateString) => {
    if (!dateString) return 'Date inconnue';
    
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },


  /**
   * ✅ CORRECTION : Créer une nouvelle vente
   */
  createSale: async (saleData) => {
    try {
      
      let payload;
      
      // ✅ Si les données sont déjà formatées (contiennent customer_name), les utiliser directement
      if (saleData.customer_name && saleData.items) {
        payload = saleData;
      } else {
        // ✅ Sinon, formater les données avec formatSaleForBackend
        payload = saleService.formatSaleForBackend(saleData);
      }
      
      // ✅ Validation finale
      const validation = saleService.validateBackendPayload(payload);
      if (!validation.isValid) {
        return {
          success: false,
          message: `Données invalides: ${validation.errors.join(', ')}`
        };
      }
      
      
      const response = await apiCall.post('/employee/sales', payload);
      
      if (response.success) {
        return response;
      }
      
      return { 
        success: false, 
        message: response.message || 'Erreur lors de la création de la vente' 
      };
    } catch (error) {
      console.error('❌ Erreur lors de la création de la vente:', error);
      return { 
        success: false, 
        message: error.message || 'Erreur de connexion' 
      };
    }
  },

  /**
   * Récupérer les détails d'une vente
   */
  getSaleById: async (saleId) => {
  try {
    
    // ✅ Essayer d'abord l'endpoint detail
    try {
      const response = await apiCall.get(`/employee/sales/${saleId}`);
      if (response.success) {
        return response;
      }
    } catch (detailError) {
      console.warn('⚠️ Endpoint detail non disponible, essai edit-info...');
    }
    
    // ✅ Fallback : utiliser l'endpoint edit-info qui existe
    try {
      const editInfoResponse = await apiCall.get(`/employee/sales/${saleId}/edit-info`);
      if (editInfoResponse.success) {
        // Retourner sous le même format que detail
        return {
          success: true,
          data: editInfoResponse.data,
          message: 'Détails récupérés via edit-info'
        };
      }
    } catch (editError) {
      console.warn('⚠️ Endpoint edit-info non disponible non plus');
    }
    
    // ✅ Si aucun endpoint ne marche, retourner une erreur explicite
    return { 
      success: false, 
      data: null, 
      message: 'Endpoints de récupération des détails non disponibles sur le serveur' 
    };
    
  } catch (error) {
    console.error('❌ Erreur lors de la récupération de la vente:', error);
    return { 
      success: false, 
      data: null, 
      message: 'Erreur de connexion' 
    };
  }
},

  /**
   * ✅ CORRECTION : Formater les articles de vente
   * Le SaleItemDto utilise aussi des @JsonProperty en snake_case
   */
  formatSaleItemForDisplay: (item) => {
  if (!item) return null;
  
  try {
    return {
      id: item.id,
      productId: item.product_id || item.productId,
      productName: item.product_name || item.productName || 'Produit inconnu',
      quantity: parseInt(item.quantity || 0),
      unitPrice: parseFloat(item.unit_price || item.unitPrice || 0),
      totalPrice: parseFloat(item.total_price || item.totalPrice || 0),
      productType: item.product_type || item.productType || 'STOCKABLE',
      isService: (item.product_type || item.productType) === 'SERVICE',
      typeIcon: (item.product_type || item.productType) === 'SERVICE' ? '🔧' : '📦',
      typeLabel: (item.product_type || item.productType) === 'SERVICE' ? 'Service' : 'Produit',
      categoryName: item.category_name || item.categoryName || 'Non catégorisé',
      imageUrl: item.image_url || item.imageUrl
    };
  } catch (error) {
    console.error('❌ Erreur formatage article:', error, item);
    return null;
  }
},
  /**
   * Mettre à jour une vente (par exemple pour les crédits)
   */
  updateSale: async (saleId, saleData) => {
    try {
      
      const response = await apiCall.put(`/manager/sales/${saleId}`, saleData);
      
      if (response.success) {
        return response;
      }
      
      return { success: false, message: response.message || 'Erreur lors de la mise à jour' };
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de la vente:', error);
      return { success: false, message: 'Erreur de connexion' };
    }
  },

  /**
   * Formater une vente pour l'affichage
   */
  
formatSaleForDisplay: (sale) => {
  if (!sale) return null;
  
  try {
    // ✅ Calculer les valeurs de crédit de manière robuste
    const isCredit = sale.is_credit || sale.isCredit || false;
    const total = parseFloat(sale.total || 0);
    const creditPaid = parseFloat(sale.credit_paid || sale.creditPaid || 0);
    
    // ✅ IMPORTANT : Utiliser creditBalance du backend si disponible
    let creditBalance;
    if (sale.credit_balance !== undefined && sale.credit_balance !== null) {
      creditBalance = parseFloat(sale.credit_balance);
    } else if (sale.creditBalance !== undefined && sale.creditBalance !== null) {
      creditBalance = parseFloat(sale.creditBalance);
    } else {
      // Fallback : calculer nous-mêmes
      creditBalance = total - creditPaid;
    }
    
    // ✅ Calculer isCreditPaid
    const isCreditPaidCalculated = isCredit && creditPaid >= total;
    const isCreditPaid = sale.is_credit_paid !== undefined 
      ? sale.is_credit_paid 
      : (sale.isCreditPaid !== undefined 
          ? sale.isCreditPaid 
          : isCreditPaidCalculated);
    
    const formatted = {
      id: sale.id,
      saleNumber: sale.sale_number || sale.saleNumber || 'N/A',
      
      // Client
      customerId: sale.customer_id || sale.customerId,
      customerName: sale.customer_name || sale.customerName || 'Client anonyme',
      customerPhone: sale.customer_phone || sale.customerPhone || '',
      customer: sale.customer ? {
        id: sale.customer.id || sale.customer_id || sale.customerId,
        name: sale.customer.name || sale.customer_name || sale.customerName || 'Client anonyme',
        phone: sale.customer.phone || sale.customer_phone || sale.customerPhone || ''
      } : {
        name: sale.customer_name || sale.customerName || 'Client anonyme',
        phone: sale.customer_phone || sale.customerPhone || ''
      },
      
      // Vendeur
      sellerId: sale.seller_id || sale.sellerId,
      sellerName: sale.seller_name || sale.sellerName || 'Vendeur',
      sellerAccess: sale.seller_access || sale.sellerAccess || 'ALL',
      seller: sale.seller ? {
        id: sale.seller.id || sale.seller_id || sale.sellerId,
        name: sale.seller.name || sale.seller_name || sale.sellerName || 'Vendeur',
        access: sale.seller.access || sale.seller_access || sale.sellerAccess || 'ALL',
        firstName: sale.seller.first_name || sale.seller.firstName,
        lastName: sale.seller.last_name || sale.seller.lastName
      } : null,
      
      // Articles
      items: sale.items || sale.sale_items || [],
      
      // Montants
      subtotal: parseFloat(sale.subtotal || 0),
      discount: parseFloat(sale.discount || 0),
      tax: parseFloat(sale.tax || 0),
      total: total,
      totalQuantity: parseInt(sale.total_quantity || sale.totalQuantity || 0),
      
      // Paiement
      paymentMethod: sale.payment_method || sale.paymentMethod || 'CASH',
      
      // ✅ CRÉDIT - Valeurs correctement formatées
      isCredit: isCredit,
      creditPaid: creditPaid,
      creditBalance: creditBalance, // ✅ Valeur du backend ou calculée
      isCreditPaid: isCreditPaid,

      isCancelled: sale.is_cancelled || false,
      cancelledAt: sale.cancelled_at,
      cancelledBy: sale.cancelled_by,
      cancellationReason: sale.cancellation_reason,
    
    // Badge de statut incluant annulation
    statusBadge: sale.is_cancelled ? '🚫 Annulée' : 
                 (sale.is_credit ? 
                   (sale.is_credit_paid ? '✅ Crédit Soldé' : '⏳ Crédit') : 
                   '✅ Payée')
  ,
      
      // Paiements multiples
      isMultiPayment: sale.is_multi_payment || sale.isMultiPayment || false,
      paymentDetails: sale.payment_details || sale.paymentDetails || [],
      
      // Analyse du contenu
      stockableItemsCount: sale.stockable_items_count || sale.stockableItemsCount || 0,
      serviceItemsCount: sale.service_items_count || sale.serviceItemsCount || 0,
      stockableRevenue: parseFloat(sale.stockable_revenue || sale.stockableRevenue || 0),
      serviceRevenue: parseFloat(sale.service_revenue || sale.serviceRevenue || 0),
      hasOnlyProducts: sale.has_only_products || sale.hasOnlyProducts || false,
      hasOnlyServices: sale.has_only_services || sale.hasOnlyServices || false,
      hasMixedTypes: sale.has_mixed_types || sale.hasMixedTypes || false,
      
      // Dates
      createdAt: sale.created_at || sale.createdAt,
      updatedAt: sale.updated_at || sale.updatedAt,
      saleDate: sale.sale_date || sale.saleDate || sale.created_at || sale.createdAt,
      
      // Notes
      notes: sale.notes || '',
      
      // Badge de paiement
      paymentBadge: saleService.getPaymentMethodLabel(sale.payment_method || sale.paymentMethod || 'CASH')
    };
    
    // Gestion des paiements multiples
    if (formatted.isMultiPayment && formatted.paymentDetails.length > 0) {
      formatted.paymentDetails = formatted.paymentDetails.map(payment => ({
        id: payment.id,
        method: payment.payment_method || payment.method,
        methodLabel: saleService.getPaymentMethodLabel(payment.payment_method || payment.method),
        amount: parseFloat(payment.amount || 0),
        reference: payment.reference_number || payment.reference,
        notes: payment.notes
      }));
      
      formatted.paymentBadge = '🔄 Paiement Mixte';
      formatted.paymentColor = 'info';
    }

    
    
    return formatted;
    
  } catch (error) {
    console.error('❌ Erreur formatage vente:', error, sale);
    return null;
  }
},

  /**
 * ✅ Annuler une vente (moins de 24h)
 * Endpoint backend: PATCH /api/employee/sales/{saleId}/cancel
 */
cancelSale: async (saleId, reason = null) => {
  try {
    
    // Validation
    if (!saleId) {
      throw new Error('ID de vente requis');
    }
    
    if (!reason || !reason.trim()) {
      throw new Error('La raison de l\'annulation est obligatoire');
    }
    
    // Payload
    const payload = {
      reason: reason.trim()
    };
    
    
    // Appel API PATCH
    const response = await apiCall.patch(`/employee/sales/${saleId}/cancel`, payload);
    
    if (response.success) {
      
      return {
        success: true,
        data: response.data,
        message: 'Vente annulée avec succès. Le stock a été rétabli.'
      };
    }
    
    return {
      success: false,
      message: response.message || 'Erreur lors de l\'annulation'
    };
    
  } catch (error) {
    console.error('❌ Erreur annulation vente:', error);
    
    // Gestion des erreurs spécifiques
    if (error.response?.status === 400) {
      const errorData = error.response.data;
      return {
        success: false,
        message: errorData?.message || 'Impossible d\'annuler cette vente'
      };
    }
    
    if (error.response?.status === 403) {
      return {
        success: false,
        message: 'Seul le créateur de la vente peut l\'annuler'
      };
    }
    
    if (error.response?.status === 404) {
      return {
        success: false,
        message: 'Vente introuvable'
      };
    }
    
    return {
      success: false,
      message: error.message || 'Erreur de connexion'
    };
  }
},

/**
 * ✅ Vérifier si une vente peut être annulée
 */
canCancelSale: (sale) => {
  if (!sale || !sale.createdAt) return false;
  
  // Vérifier si moins de 24h
  const saleDate = new Date(sale.createdAt);
  const now = new Date();
  const hoursDiff = (now - saleDate) / (1000 * 60 * 60);
  
  return hoursDiff < 24;
},

/**
 * ✅ Obtenir les heures restantes pour annuler une vente
 */
getHoursRemainingToCancel: (sale) => {
  if (!sale || !sale.createdAt) return 0;
  
  const saleDate = new Date(sale.createdAt);
  const now = new Date();
  const hoursDiff = (now - saleDate) / (1000 * 60 * 60);
  
  return Math.max(0, 24 - hoursDiff);
},

   /**
   * ✅ NOUVELLE MÉTHODE : Obtenir les statistiques par type de produit
   */
  getSalesStatsByType: async (startDate = null, endDate = null) => {
    try {
      
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await apiCall.get('/employee/sales/stats/by-type', params);
      
      if (response.success) {
        const stats = {
          stockableRevenue: parseFloat(response.data.stockableRevenue || 0),
          serviceRevenue: parseFloat(response.data.serviceRevenue || 0),
          stockableCount: parseInt(response.data.stockableCount || 0),
          serviceCount: parseInt(response.data.serviceCount || 0),
          totalRevenue: parseFloat(response.data.totalRevenue || 0),
          stockablePercentage: parseFloat(response.data.stockablePercentage || 0),
          servicePercentage: 100 - parseFloat(response.data.stockablePercentage || 0),
          
          // Formatage pour affichage
          formattedStockableRevenue: saleService.formatCurrency(response.data.stockableRevenue || 0),
          formattedServiceRevenue: saleService.formatCurrency(response.data.serviceRevenue || 0),
          formattedTotalRevenue: saleService.formatCurrency(response.data.totalRevenue || 0)
        };
        
        return { success: true, data: stats };
      }
      
      return { success: false, data: null };
      
    } catch (error) {
      console.error('❌ Erreur récupération stats par type:', error);
      return { success: false, data: null };
    }
  },

  /**
   * ✅ UTILITAIRES pour les types de produits
   */
  getProductTypeLabel: (type) => {
    switch(type) {
      case 'STOCKABLE': return 'Produit';
      case 'SERVICE': return 'Service';
      default: return 'Inconnu';
    }
  },

  getProductTypeIcon: (type) => {
    switch(type) {
      case 'STOCKABLE': return '📦';
      case 'SERVICE': return '🔧';
      default: return '❓';
    }
  },

  getProductTypeBadgeClass: (type) => {
    switch(type) {
      case 'STOCKABLE': return 'badge bg-primary';
      case 'SERVICE': return 'badge bg-info';
      default: return 'badge bg-secondary';
    }
  },

  /**
   * Calculer le total d'un panier
   */
  calculateCartTotal: (cartItems) => {
    if (!Array.isArray(cartItems)) return 0;
    
    return cartItems.reduce((total, item) => {
      const price = item.price || item.unitPrice || 0;
      const quantity = item.quantity || 1;
      const discount = item.discount || 0;
      
      const itemTotal = (price * quantity) - discount;
      return total + Math.max(0, itemTotal);
    }, 0);
  },

  /**
   * Valider les données d'une vente
   */
  validateSaleData: (saleData) => {
    const errors = [];
    
    if (!saleData.items || saleData.items.length === 0) {
      errors.push('Au moins un produit doit être ajouté à la vente');
    }
    
    if (!saleData.paymentMethod) {
      errors.push('Un mode de paiement doit être sélectionné');
    }
    
    if (saleData.isCredit && (!saleData.customer || !saleData.customer.name)) {
      errors.push('Un client doit être sélectionné pour une vente à crédit');
    }
    
    // Valider chaque item
    if (saleData.items) {
      saleData.items.forEach((item, index) => {
        if (!item.productId) {
          errors.push(`Produit manquant à la ligne ${index + 1}`);
        }
        
        if (!item.quantity || item.quantity <= 0) {
          errors.push(`Quantité invalide à la ligne ${index + 1}`);
        }
        
        if (!item.price || item.price <= 0) {
          errors.push(`Prix invalide à la ligne ${index + 1}`);
        }
      });
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
 * Vérifier si une vente peut être modifiée
 */
canEditSale: async (saleId) => {
  try {
    
    const response = await apiCall.get(`/employee/sales/${saleId}/can-edit`);
    
    if (response.success) {
      return {
        success: true,
        canEdit: response.data,
        message: response.message
      };
    }
    
    return {
      success: false,
      canEdit: false,
      message: response.message || 'Impossible de vérifier les droits'
    };
    
  } catch (error) {
    console.error('❌ Erreur vérification droits modification:', error);
    return {
      success: false,
      canEdit: false,
      message: 'Erreur de connexion'
    };
  }
},

/**
 * Modifier une vente existante (version complète)
 */
updateExistingSale: async (saleId, saleData) => {
  try {
    
    // Validation des données
    if (!saleId) {
      throw new Error('ID de vente requis');
    }
    
    if (!saleData.customerName || !saleData.customerName.trim()) {
      throw new Error('Nom du client requis');
    }
    
    if (!saleData.items || saleData.items.length === 0) {
      throw new Error('Au moins un article est requis');
    }
    
    if (!saleData.paymentMethod) {
      throw new Error('Mode de paiement requis');
    }
    
    // Formater les données pour le backend selon UpdateSaleRequest.java
    const backendPayload = {
      customerName: saleData.customerName.trim(),
      customerPhone: saleData.customerPhone || null,
      items: saleData.items.map(item => ({
        productId: item.productId,
        quantity: parseInt(item.quantity),
        unitPrice: parseFloat(item.unitPrice || item.price)
      })),
      discount: saleData.discount ? parseFloat(saleData.discount) : 0,
      tax: saleData.tax ? parseFloat(saleData.tax) : 0,
      paymentMethod: saleData.paymentMethod,
      isCredit: Boolean(saleData.isCredit),
      notes: saleData.notes || null,
      modificationReason: saleData.modificationReason || 'Correction'
    };
    
    
    const response = await apiCall.put(`/employee/sales/${saleId}`, backendPayload);
    
    if (response.success) {
      return {
        success: true,
        data: saleService.formatSaleForDisplay(response.data),
        message: 'Vente modifiée avec succès'
      };
    }
    
    return {
      success: false,
      message: response.message || 'Erreur lors de la modification'
    };
    
  } catch (error) {
    console.error('❌ Erreur modification vente:', error);
    
    // Messages d'erreur plus spécifiques
    if (error.response?.status === 403) {
      return {
        success: false,
        message: 'Vous n\'avez pas l\'autorisation de modifier cette vente'
      };
    }
    
    if (error.response?.status === 400) {
      return {
        success: false,
        message: error.response.data?.message || 'Données de modification invalides'
      };
    }
    
    return {
      success: false,
      message: error.message || 'Erreur de connexion'
    };
  }
},

/**
 * Modifier une vente (version simplifiée pour SalesList.js existant)
 */
updateSaleBasic: async (saleId, updateData) => {
  try {
    
    // ✅ VALIDATION DES DONNÉES D'ENTRÉE
    if (!saleId) {
      throw new Error('ID de vente requis');
    }
    
    if (!updateData.customerName || !updateData.customerName.trim()) {
      throw new Error('Nom du client requis');
    }
    
    if (!updateData.items || updateData.items.length === 0) {
      throw new Error('Au moins un article est requis');
    }
    
    if (!updateData.paymentMethod) {
      throw new Error('Mode de paiement requis');
    }
    
    // ✅ VALIDATION ET FORMATAGE DES ARTICLES
    const validatedItems = updateData.items.map((item, index) => {
      
      if (!item.productId) {
        console.error(`❌ Item ${index} n'a pas de productId:`, item);
        throw new Error(`Article ${index + 1}: ID produit manquant`);
      }
      
      const quantity = parseInt(item.quantity);
      if (!quantity || quantity <= 0) {
        console.error(`❌ Item ${index} quantité invalide:`, item.quantity);
        throw new Error(`Article ${index + 1}: Quantité invalide`);
      }
      
      const unitPrice = parseFloat(item.unitPrice || item.price);
      if (isNaN(unitPrice) || unitPrice <= 0) {
        console.error(`❌ Item ${index} prix invalide:`, item);
        throw new Error(`Article ${index + 1}: Prix invalide`);
      }
      
      // ✅ FORMAT EXACT pour SaleItemDto (avec @JsonProperty)
      return {
        product_id: item.productId, // snake_case pour @JsonProperty
        quantity: quantity,
        unit_price: unitPrice
      };
    });
    
    // ✅ PAYLOAD SELON UpdateSaleRequest.java (respectant les annotations @JsonProperty)
    const backendPayload = {
      customerName: updateData.customerName.trim(), // Pas de @JsonProperty
      customerPhone: updateData.customerPhone || null, // Pas de @JsonProperty
      paymentMethod: updateData.paymentMethod, // Pas de @JsonProperty
      isCredit: Boolean(updateData.isCredit), // Pas de @JsonProperty
      discount: parseFloat(updateData.discount || 0),
      tax: parseFloat(updateData.tax || 0),
      notes: updateData.notes || '',
      modificationReason: updateData.modificationReason || 'Correction via interface',
      items: validatedItems
    };
    
    
    // ✅ VALIDATION FINALE DU PAYLOAD
    const validation = saleService.validateUpdatePayload(backendPayload);
    if (!validation.isValid) {
      console.error('❌ Validation payload échoué:', validation.errors);
      throw new Error('Données invalides: ' + validation.errors.join(', '));
    }
    
    // Appel API avec le bon endpoint
    const response = await apiCall.put(`/employee/sales/${saleId}`, backendPayload);
    
    if (response.success) {
      return {
        success: true,
        data: saleService.formatSaleForDisplay(response.data),
        message: 'Vente modifiée avec succès'
      };
    }
    
    return {
      success: false,
      message: response.message || 'Erreur lors de la modification'
    };
    
  } catch (error) {
    console.error('❌ Erreur modification vente basique:', error);
    
    // Gestion des erreurs spécifiques
    if (error.response?.status === 400) {
      const errorData = error.response.data;
      let errorMessage = 'Données de modification invalides';
      
      if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      // Si c'est une erreur de validation avec détails
      if (errorData?.error_code === 'VALIDATION_ERROR' && errorData?.data?.validationErrors) {
        const validationErrors = errorData.data.validationErrors
          .map(err => `${err.field}: ${err.message}`)
          .join(', ');
        errorMessage = `Validation échouée: ${validationErrors}`;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
    
    if (error.response?.status === 403) {
      return {
        success: false,
        message: 'Seul le créateur de la vente peut la modifier'
      };
    }
    
    return {
      success: false,
      message: error.message || 'Erreur de connexion'
    };
  }
},

/**
 * ✅ NOUVEAU: Validation du payload pour UpdateSaleRequest
 */
validateUpdatePayload: (payload) => {
  const errors = [];
  
  // Validation des champs requis selon UpdateSaleRequest.java
  if (!payload.customerName?.trim()) {
    errors.push('customerName requis');
  }
  
  if (!payload.paymentMethod) {
    errors.push('paymentMethod requis');
  }
  
  if (!payload.modificationReason?.trim()) {
    errors.push('modificationReason requis');
  }
  
  // Validation des articles
  if (!Array.isArray(payload.items) || payload.items.length === 0) {
    errors.push('items requis et non vide');
  } else {
    payload.items.forEach((item, index) => {
      if (!item.product_id) {
        errors.push(`items[${index}].product_id requis`);
      }
      if (!item.quantity || item.quantity <= 0) {
        errors.push(`items[${index}].quantity invalide`);
      }
      if (item.unit_price === undefined || item.unit_price <= 0) {
        errors.push(`items[${index}].unit_price invalide`);
      }
    });
  }
  
  // Validation des montants
  if (payload.discount !== undefined && payload.discount < 0) {
    errors.push('discount ne peut pas être négatif');
  }
  
  if (payload.tax !== undefined && payload.tax < 0) {
    errors.push('tax ne peut pas être négatif');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
},

/**
 * Obtenir les informations de modification d'une vente
 */
getSaleEditInfo: async (saleId) => {
  try {
    
    const response = await apiCall.get(`/employee/sales/${saleId}/edit-info`);
    
    if (response.success) {
      return {
        success: true,
        data: response.data,
        message: 'Informations récupérées'
      };
    }
    
    return {
      success: false,
      message: response.message || 'Erreur lors de la récupération'
    };
    
  } catch (error) {
    console.error('❌ Erreur récupération infos modification:', error);
    return {
      success: false,
      message: 'Erreur de connexion'
    };
  }
},

/**
 * Formater une vente pour l'édition
 */
formatSaleForEdit: (sale) => {
  if (!sale) return null;
  
  return {
    id: sale.id,
    saleNumber: sale.saleNumber,
    customerName: sale.customer?.name || '',
    customerPhone: sale.customer?.phone || '',
    items: sale.items?.map(item => ({
      productId: item.productId,
      productName: item.productName,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      price: item.unitPrice, // Alias pour compatibilité
      stock: item.availableStock || 999 // Valeur par défaut
    })) || [],
    subtotal: sale.subtotal || 0,
    discount: sale.discount || 0,
    tax: sale.tax || 0,
    total: sale.total || 0,
    paymentMethod: sale.paymentMethod || 'CASH',
    isCredit: sale.isCredit || false,
    notes: sale.notes || '',
    createdAt: sale.createdAt,
    seller: sale.seller
  };
},

 /**
 * Méthodes de paiement disponibles
 */
getPaymentMethods: () => [
  { value: 'CASH', label: 'Espèces', icon: '💵', color: 'success' },
  { value: 'WAVE', label: 'Wave', icon: '📱', color: 'primary' },
  { value: 'ORANGE_MONEY', label: 'Orange Money', icon: '🍊', color: 'warning' },
  { value: 'CARD', label: 'Carte bancaire', icon: '💳', color: 'info' }, // ← NOUVEAU
  { value: 'BANK_TRANSFER', label: 'Virement', icon: '🏦', color: 'secondary' },
  { value: 'CHECK', label: 'Chèque', icon: '📋', color: '#000000' },
  { value: 'MIXED', label: 'Paiement miste', icon: '🔄', color: '#000000' },
  { value: 'CREDIT', label: 'Crédit', icon: '⏳', color: 'danger' } // ← Changé l'icône pour différencier
],

  /**
   * ✅ NOUVEAU : Map des méthodes de paiement pour l'affichage
   */
  getPaymentMethodsMap: () => {
    const methods = {};
    saleService.getPaymentMethods().forEach(method => {
      methods[method.value] = `${method.icon} ${method.label}`;
    });
    return methods;
  },

  validateProductsAvailability: async (items) => {
    try {
      
      const response = await apiCall.post('/employee/sales/validate', { items });
      
      if (response.success) {
        return {
          success: true,
          canProceed: response.data.canProceed,
          errors: response.data.errors || [],
          warnings: response.data.warnings || []
        };
      }
      
      return {
        success: false,
        canProceed: false,
        errors: ['Erreur lors de la validation'],
        warnings: []
      };
    } catch (error) {
      console.error('❌ Erreur validation produits:', error);
      return {
        success: false,
        canProceed: false,
        errors: [error.message || 'Erreur de connexion'],
        warnings: []
      };
    }
  },

  /**
   * ✅ MÉTHODE AMÉLIORÉE : Créer une vente avec validation des types
   */
  createSaleWithValidation: async (saleData) => {
    try {
      
      // 1. Valider la disponibilité des produits
      const validation = await saleService.validateProductsAvailability(saleData.items);
      
      if (!validation.canProceed) {
        return {
          success: false,
          message: 'Validation échouée',
          errors: validation.errors,
          warnings: validation.warnings
        };
      }

      // 2. Afficher les warnings s'il y en a
      if (validation.warnings.length > 0) {
        console.warn('⚠️ Warnings lors de la validation:', validation.warnings);
      }

      // 3. Créer la vente si validation OK
      return await saleService.createSale(saleData);
      
    } catch (error) {
      console.error('❌ Erreur création vente avec validation:', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la création'
      };
    }
  },

  /**
 * ✅ NOUVELLE MÉTHODE : Créer une vente avec paiements multiples
 */
createSaleWithMultiPayment: async (requestData) => {
  try {
    
    // ✅ Validation minimale côté frontend
    if (!requestData.saleRequest) {
      throw new Error('Données de vente manquantes');
    }
    
    if (!requestData.saleRequest.customer_name) {
      throw new Error('Nom du client requis');
    }
    
    if (!requestData.saleRequest.items || requestData.saleRequest.items.length === 0) {
      throw new Error('Aucun article dans le panier');
    }
    
    if (!requestData.multiPayment || !requestData.multiPayment.payments) {
      throw new Error('Données de paiement manquantes');
    }
    
    // Envoi direct au backend sans autre transformation
    const response = await apiCall.post('/employee/sales/multi-payment', requestData);
    
    if (response.success) {
      return {
        success: true,
        data: response.data,
        message: 'Vente créée avec paiements multiples avec succès'
      };
    }
    
    return {
      success: false,
      message: response.message || 'Erreur lors de la création'
    };
    
  } catch (error) {
    console.error('❌ Erreur service multi-paiement:', error);
    
    let errorMessage = 'Erreur de connexion';
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.error('📝 Message d\'erreur final:', errorMessage);
    
    return {
      success: false,
      message: errorMessage
    };
  }
},



/**
 * ✅ NOUVELLE MÉTHODE : Calculer le total d'une vente
 */
calculateSaleTotal: (saleRequest) => {
  
  if (!saleRequest) {
    return 0;
  }
  
  let subtotal = 0;
  
  // Gérer les deux formats possibles d'items
  if (saleRequest.items && Array.isArray(saleRequest.items)) {
    subtotal = saleRequest.items.reduce((sum, item) => {
      // Priorité à unitPrice, puis price
      const price = parseFloat(item.unitPrice || item.price || 0);
      const quantity = parseInt(item.quantity || 1);
      const itemTotal = price * quantity;
      
     
      
      return sum + itemTotal;
    }, 0);
  }
  
  // Application remise et taxes
  const discount = parseFloat(saleRequest.discount || 0);
  const tax = parseFloat(saleRequest.tax || 0);
  
  const finalTotal = Math.max(0, subtotal - discount + tax);
  
  
  return finalTotal;
},


/**
 * ✅ NOUVELLE MÉTHODE : Valider les données de paiements multiples
 */
validateMultiPaymentData: (saleData) => {
  const errors = [];
  
  // Vérifier la structure de base
  if (!saleData.saleRequest) {
    errors.push('Données de vente manquantes');
    return { isValid: false, errors };
  }
  
  if (!saleData.multiPayment) {
    errors.push('Données de paiement manquantes');
    return { isValid: false, errors };
  }
  
  // Calculer le total attendu
  const expectedTotal = saleService.calculateSaleTotal(saleData.saleRequest);
  
  if (expectedTotal <= 0) {
    errors.push('Le total de la vente doit être supérieur à 0');
  }
  
  const { payments } = saleData.multiPayment;
  
  if (!payments || payments.length === 0) {
    errors.push('Au moins un paiement est requis');
  } else {
    let totalPayments = 0;
    
    // Valider chaque paiement
    payments.forEach((payment, index) => {
      if (!payment.method) {
        errors.push(`Méthode de paiement manquante pour le paiement ${index + 1}`);
      }
      
      const amount = parseFloat(payment.amount);
      if (!payment.amount || amount <= 0 || isNaN(amount)) {
        errors.push(`Montant invalide pour le paiement ${index + 1}: ${payment.amount}`);
      } else {
        totalPayments += amount;
      }
    });
    
    
    // ✅ Vérification avec tolérance pour les arrondis
    if (expectedTotal > 0 && Math.abs(totalPayments - expectedTotal) > 0.01) {
      errors.push(`Le total des paiements (${totalPayments}) ne correspond pas au total de la vente (${expectedTotal})`);
    }
  }
  
  const result = {
    isValid: errors.length === 0,
    errors,
    expectedTotal // ✅ Retourner le total pour debug
  };
  
  return result;
},

/**
 * ✅ MÉTHODES UTILITAIRES pour les paiements
 */
getPaymentMethodLabel: (method) => {
  const labels = {
    'CASH': 'Espèces',
    'WAVE': 'Wave',
    'ORANGE_MONEY': 'Orange Money',
    'CARD': 'Carte bancaire',
    'BANK_TRANSFER': 'Virement',
    'CREDIT': 'À crédit',
    'MIXED': 'Paiement mixte'
  };
  return labels[method] || method;
},

getPaymentMethodIcon: (method) => {
  const icons = {
    'CASH': '💵',
    'WAVE': '📱',
    'ORANGE_MONEY': '🍊',
    'CARD': '💳',
    'BANK_TRANSFER': '🏦',
    'CREDIT': '💳',
    'MIXED': '🔄'
  };
  return icons[method] || '💰';
},

getPaymentMethodColor: (method) => {
  const colors = {
    'CASH': 'success',
    'WAVE': 'primary',
    'ORANGE_MONEY': 'warning',
    'CARD': 'info',
    'BANK_TRANSFER': 'secondary',
    'CREDIT': 'danger',
    'MIXED': 'info'
  };
  return colors[method] || 'secondary';
},

/**
 * ✅ NOUVELLE MÉTHODE : Formater les données pour l'API multi-paiement
 */
// ✅ Dans saleService.js, ajoutez cette méthode :

formatMultiPaymentForBackend: (saleData, payments) => {
  
  // ✅ Calculer le total de la vente de manière cohérente
  const expectedTotal = saleService.calculateSaleTotal(saleData);
  
  // Formater la vente de base
  const saleRequest = saleService.formatSaleForBackend(saleData);
  
  // ✅ CORRECTION: S'assurer que le total est dans saleRequest
  saleRequest.expectedTotal = expectedTotal;
  
  // Formater les paiements
  const multiPayment = {
    payments: payments.map(payment => ({
      method: payment.method,
      amount: parseFloat(payment.amount),
      reference: payment.reference || null,
      notes: payment.notes || null
    })),
    expectedTotal: expectedTotal
  };
  
  const result = {
    saleRequest,
    multiPayment
  };
  
  return result;
},

/**
 * ✅ NOUVELLE MÉTHODE : Formatter une vente avec paiements multiples pour l'affichage
 */
formatMultiPaymentSale: (sale) => {
  // Commencer par le formatage standard
  const formatted = saleService.formatSaleForDisplay(sale);
  
  // Si c'est un paiement multiple, ajouter les détails
  if (sale.is_multi_payment && sale.payment_details) {
    formatted.isMultiPayment = true;
    formatted.paymentDetails = sale.payment_details.map(payment => ({
      id: payment.id,
      method: payment.payment_method,
      methodLabel: payment.payment_method_label || saleService.getPaymentMethodLabel(payment.payment_method),
      methodIcon: payment.payment_method_icon || saleService.getPaymentMethodIcon(payment.payment_method),
      amount: payment.amount,
      formattedAmount: saleService.formatCurrency(payment.amount),
      reference: payment.reference_number,
      notes: payment.notes
    }));
    
    // Badge spécial pour paiement mixte
    formatted.paymentBadge = '🔄 Paiement Mixte';
    formatted.paymentColor = 'info';
  } else {
    formatted.isMultiPayment = false;
  }
  
  return formatted;
},

/**
 * ✅ NOUVEAU : Récupérer les statistiques de paiements depuis sale_payments
 */
getPaymentStatistics: async (startDate = null, endDate = null) => {
    try {
      
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      // ✅ Le backend filtre automatiquement les paiements selon l'utilisateur
      const response = await apiCall.get('/employee/payments/statistics', params);
      
      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      }
      
      return {
        success: false,
        data: null,
        message: response.message || 'Erreur lors de la récupération'
      };
      
    } catch (error) {
      console.error('❌ Erreur récupération stats paiements:', error);
      return {
        success: false,
        data: null,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

/**
 * ✅ NOUVEAU : Calculer le solde de caisse basé sur sale_payments
 */
calculateCashBalanceFromPayments: async () => {
    try {
      
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);
      
      // ✅ Récupérer les statistiques de paiement du jour (déjà filtrées par le backend)
      const stats = await saleService.getPaymentStatistics(
        startOfDay.toISOString(),
        endOfDay.toISOString()
      );
      
      if (stats.success && stats.data) {
        
        // Trouver les paiements en espèces
        const paymentMethodStats = stats.data.paymentMethodStats || [];
        const cashPayments = paymentMethodStats.find(
          method => method.paymentMethod === 'CASH' || method.payment_method === 'CASH'
        );
        
        const cashAmount = cashPayments ? 
          parseFloat(cashPayments.totalAmount || cashPayments.total_amount || 0) : 0;
        
        const transactionCount = cashPayments ? 
          (cashPayments.transactionCount || cashPayments.transaction_count || 0) : 0;
        
        
        
        return {
          success: true,
          cashAmount,
          transactionCount,
          allMethods: paymentMethodStats
        };
      }
      
      console.warn('⚠️ Aucune statistique de paiement disponible');
      return {
        success: false,
        cashAmount: 0,
        transactionCount: 0
      };
      
    } catch (error) {
      console.error('❌ Erreur calcul solde caisse:', error);
      return {
        success: false,
        cashAmount: 0,
        transactionCount: 0
      };
    }
  },


  /**
   * Obtenir les informations sur une méthode de paiement
   */
  getPaymentMethodInfo: (methodValue) => {
    return saleService.getPaymentMethods().find(method => method.value === methodValue) || null;
  },

  getSaleStatuses: () => [
    { value: 'PAID', label: 'Payé', color: 'success', icon: '✅' },
    { value: 'CREDIT', label: 'Crédit', color: 'warning', icon: '⏳' },
    { value: 'CREDIT_PAID', label: 'Crédit soldé', color: 'success', icon: '✅' }
  ],

};


export default saleService;